import React, { useEffect, useCallback } from 'react'
import cx from 'classnames'
import keyboardKey from 'keyboard-key'
import { animated, useSpring } from 'react-spring'
import Heading from '../../atoms/Typography/Heading/Heading'
import ContextMenu from '../../molecules/ContextMenu/ContextMenu'
import CloseIcon from '../../atoms/Svgs/Close'
import styles from './FocusMode.module.scss'
import { eventStack } from '../../../../utils'

interface MenuOption {
  label: string
  element?: React.ReactNode
  onSelect: Function
  isHidden: boolean
  subMenuOptions?: Array<string>
}
type TFocusModeProps = {
  isOpen: boolean
  titlePrefix?: React.ReactNode
  title: string
  onClose?: () => void
  ContentComponent?: React.ComponentType
  SidebarComponent?: React.ComponentType
  menuOptions?: MenuOption[]
  disableScroll?: boolean
}

const FocusMode: React.FC<TFocusModeProps> = ({
  isOpen,
  titlePrefix,
  title,
  onClose = () => {},
  ContentComponent,
  SidebarComponent,
  menuOptions = [],
  disableScroll = false,
}) => {
  const maybeCloseFocusMode = useCallback(
    (e: Event) => {
      if (e instanceof KeyboardEvent && keyboardKey.getCode(e) === keyboardKey.Escape) {
        onClose()
      }
    },
    [onClose],
  )

  const subShortcuts = useCallback(() => {
    eventStack.sub('keydown', maybeCloseFocusMode)
  }, [maybeCloseFocusMode])

  const unsubShortcuts = useCallback(() => {
    eventStack.unsub('keydown', maybeCloseFocusMode)
  }, [maybeCloseFocusMode])

  useEffect(() => {
    if (isOpen) {
      subShortcuts()
    } else {
      unsubShortcuts()
    }
  }, [isOpen, subShortcuts, unsubShortcuts])

  const style = useSpring({
    reverse: !isOpen,
    config: { mass: 1, tension: 50, friction: 10, duration: 150 },
    to: { transform: 'translateY(0)' },
    from: { transform: 'translateY(100%)' },
  })

  const classes = cx(styles.root, {
    [styles['has-sidebar']]: !!SidebarComponent,
    [styles['disable-scroll']]: disableScroll,
  })

  return (
    <animated.div className={classes} style={style}>
      <div className={styles.container}>
        <div className={styles.header}>
          {menuOptions.length > 0 ? (
            <div className={styles.menu}>
              <ContextMenu items={menuOptions} position="left" />
            </div>
          ) : (
            <div dangerouslySetInnerHTML={{ __html: '<span>&nbsp;</span>' }} />
          )}
          {titlePrefix && title ? (
            <span className={styles['has-title-prefix']}>
              {titlePrefix}
              <Heading as="h3">{title}</Heading>
            </span>
          ) : (
            <Heading as="h3">{title}</Heading>
          )}
          <button className={styles.close} onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
      </div>
      <div className={styles.body}>
        {ContentComponent && <div className={styles.content}>{<ContentComponent />}</div>}
        {SidebarComponent && <aside className={styles.sidebar}>{<SidebarComponent />}</aside>}
      </div>
    </animated.div>
  )
}

export default FocusMode
